import type { ProviderCoverageType } from "@workspace/api-client-react";
import { isUsCountryName, regionStatesForName } from "@workspace/us-regions";

/**
 * Services Marketplace label maps + ordering for the web app.
 *
 * The enums and labels are single-sourced in `@workspace/provider-labels`
 * (also consumed by the server schema and the mobile app), so the surfaces
 * cannot drift — re-exported here so existing imports keep working.
 */
export {
  PROVIDER_CATEGORIES,
  PROVIDER_CATEGORY_LABELS,
  PROVIDER_LEAD_TYPES,
  PROVIDER_LEAD_TYPE_LABELS,
  PROVIDER_COVERAGE_TYPES,
  PROVIDER_COVERAGE_LABELS,
  categoryLabel,
  leadTypeLabel,
  coverageLabel,
} from "@workspace/provider-labels";

/** Common aircraft categories providers serve (free-text on the model). */
export const PROVIDER_AIRCRAFT_CATEGORIES = [
  "Turboprop",
  "Light Jet",
  "Midsize Jet",
  "Super Midsize Jet",
  "Large Cabin Jet",
  "Ultra Long Range Jet",
  "Helicopter",
  "Piston",
] as const;

// US region names → state codes come from the shared @workspace/us-regions
// map (also consumed by the api-server assistant and the mobile matcher), so
// the surfaces cannot drift. Coverage regions are stored as free text
// (e.g. "Southeast", "West"), so matching is case-insensitive by name.

/**
 * Whether a provider's declared coverage includes the given US state code.
 * Used by the Near Me "serves your area" complement: providers with no listed
 * business address can still fully serve the visitor's area by coverage.
 *
 * Conservative on ambiguity — an empty coverage list for the declared type
 * means we can't verify a match, so we return false rather than guess.
 */
export function providerCoversState(
  provider: {
    coverageType: ProviderCoverageType;
    coverageRegions: string[];
    coverageStates: string[];
    coverageCountries: string[];
  },
  stateCode: string,
): boolean {
  const state = stateCode.trim().toUpperCase();
  if (!state) return false;
  switch (provider.coverageType) {
    case "global":
      return true;
    case "country":
      return provider.coverageCountries.some((c) => isUsCountryName(c));
    case "regional":
      return provider.coverageRegions.some((r) =>
        regionStatesForName(r).includes(state),
      );
    case "state":
      return provider.coverageStates.some((s) => s.trim().toUpperCase() === state);
    default:
      return false;
  }
}

/**
 * Human-readable summary of a provider's geographic coverage, e.g.
 * "Global", "Nationwide (US)", "TX, FL, AZ", or "Northeast Region".
 */
export function coverageSummary(provider: {
  coverageType: ProviderCoverageType;
  coverageRegions: string[];
  coverageStates: string[];
  coverageCountries: string[];
}): string {
  switch (provider.coverageType) {
    case "global":
      return "Global";
    case "country":
      return provider.coverageCountries.length
        ? provider.coverageCountries.join(", ")
        : "Nationwide";
    case "regional":
      return provider.coverageRegions.length
        ? provider.coverageRegions.join(", ")
        : "Regional";
    case "state":
      return provider.coverageStates.length
        ? provider.coverageStates.join(", ")
        : "State";
  }
}
